import type { eyeRefractionType } from '../types/eyeRefraction';
import type { infantOutcomeType, resultType } from '../types/result';
import { withLegacyChromaticForResult } from './resultChromaticNormalize';
import { hasOvalRefractionData } from './ovalResultLabel';

// Picks right or left eye metrics from stored test JSON (camelCase or legacy snake_case) for safe UI rendering.
export function pickEyeResult(
  result: resultType,
  side: 'right' | 'left',
): { meters: string; feet: string; dec: string; size: number } {
  const raw = result as unknown as Record<string, unknown>;
  const fromCamel = side === 'right' ? raw.rightEye : raw.leftEye;
  const fromSnake = side === 'right' ? raw.right_eye : raw.left_eye;
  const fromPascal = side === 'right' ? raw.RightEye : raw.LeftEye;
  const eye = (fromCamel ?? fromSnake ?? fromPascal) as Record<string, unknown> | null | undefined;
  if (!eye || typeof eye !== 'object') {
    return { meters: '', feet: '', dec: '', size: 0 };
  }
  const sizeRaw = eye.size;
  const sizeNum =
    typeof sizeRaw === 'number' && !Number.isNaN(sizeRaw)
      ? sizeRaw
      : typeof sizeRaw === 'string'
      ? Number(sizeRaw) || 0
      : 0;
  return {
    meters: String(eye.meters ?? ''),
    feet: String(eye.feet ?? ''),
    dec: String(eye.dec ?? ''),
    size: sizeNum,
  };
}

// Returns whether a stored test result has enough data to show in history (supports legacy snake_case eye keys).
export function hasRenderableTestResult(result: resultType): boolean {
  const right = pickEyeResult(result, 'right');
  const left = pickEyeResult(result, 'left');
  const hasEyeMetrics =
    Boolean(right.meters || right.feet || right.dec || right.size) ||
    Boolean(left.meters || left.feet || left.dec || left.size);

  return Boolean(
    result.distance ||
      result.resume ||
      result.date ||
      hasEyeMetrics ||
      hasOvalRefractionData(result) ||
      Boolean(result.infantOutcome),
  );
}

// Coerces per-eye refraction branch from stored JSON.
function coerceRefractionBranch(raw: unknown): eyeRefractionType | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const branch = raw as Record<string, unknown>;
  const cylinder = optionalNumber(branch.cylinder);
  const axis = optionalNumber(branch.axis);
  const ovalMinorRatio = optionalNumber(branch.ovalMinorRatio);
  const ovalAxisDeg = optionalNumber(branch.ovalAxisDeg);
  if (cylinder === undefined || axis === undefined || ovalMinorRatio === undefined || ovalAxisDeg === undefined) {
    return undefined;
  }
  return { cylinder, axis, ovalMinorRatio, ovalAxisDeg };
}

// Reads per-eye refraction from camelCase or snake_case keys.
function pickRefractionBranch(base: Record<string, unknown>, side: 'right' | 'left'): unknown {
  if (side === 'right') {
    return base.rightEyeRefraction ?? base.right_eye_refraction ?? base.RightEyeRefraction;
  }
  return base.leftEyeRefraction ?? base.left_eye_refraction ?? base.LeftEyeRefraction;
}

// Parses a JSON string into an object or returns object inputs unchanged for normalization.
function parseMaybeJsonObject(value: unknown): Record<string, unknown> {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value.trim() || '{}');
      return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

// Uses nested `.result` when present (legacy wrappers); otherwise returns the top-level payload.
function unwrapStoredResultPayload(raw: unknown): Record<string, unknown> {
  const root = parseMaybeJsonObject(raw);
  const inner = root.result;
  if (inner !== null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return root;
}

// Reads eye metrics from camelCase, snake_case, or PascalCase keys on stored JSON.
function pickEyeBranch(base: Record<string, unknown>, side: 'right' | 'left'): unknown {
  if (side === 'right') {
    return base.rightEye ?? base.right_eye ?? base.RightEye ?? base.RIGHT_EYE;
  }
  return base.leftEye ?? base.left_eye ?? base.LeftEye ?? base.LEFT_EYE;
}

// Builds a typed eye metric object from an arbitrary branch value.
function coerceEyeBranch(raw: unknown): resultType['rightEye'] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { meters: '', feet: '', dec: '', size: 0 };
  }
  const eye = raw as Record<string, unknown>;
  const sizeRaw = eye.size;
  const sizeNum =
    typeof sizeRaw === 'number' && !Number.isNaN(sizeRaw)
      ? sizeRaw
      : typeof sizeRaw === 'string'
      ? Number(sizeRaw) || 0
      : 0;
  return {
    meters: String(eye.meters ?? ''),
    feet: String(eye.feet ?? ''),
    dec: String(eye.dec ?? ''),
    size: sizeNum,
  };
}

const VALID_PATIENT_TEST_TYPES = new Set(['adult', 'child', 'infant']);

// Coerces infantOutcome from stored JSON (camelCase or snake_case).
function coerceInfantOutcome(raw: unknown): infantOutcomeType | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }

  const branch = raw as Record<string, unknown>;
  const smallestTrackedSize =
    optionalNumber(branch.smallestTrackedSize) ?? optionalNumber(branch.smallest_tracked_size);
  const lossSize = optionalNumber(branch.lossSize) ?? optionalNumber(branch.loss_size) ?? 0;
  const recoverySize = optionalNumber(branch.recoverySize) ?? optionalNumber(branch.recovery_size);
  const meters = branch.meters !== undefined && branch.meters !== null ? String(branch.meters) : undefined;
  const feet = branch.feet !== undefined && branch.feet !== null ? String(branch.feet) : undefined;
  const dec = branch.dec !== undefined && branch.dec !== null ? String(branch.dec) : undefined;

  if (
    smallestTrackedSize === undefined &&
    lossSize === 0 &&
    (recoverySize === undefined || recoverySize === 0) &&
    !meters &&
    !feet &&
    !dec
  ) {
    return undefined;
  }

  return {
    smallestTrackedSize: smallestTrackedSize ?? 0,
    lossSize,
    recoverySize: recoverySize && recoverySize > 0 ? recoverySize : undefined,
    meters,
    feet,
    dec,
  };
}

// Parses optional numeric fields from API variants (number or numeric string).
function optionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

// Coerces doctor history API payloads (JSON string, nested result, legacy keys) into resultType for UI.
export function normalizeStoredTestResult(raw: unknown): resultType {
  const base = unwrapStoredResultPayload(raw);

  const distance = base.distance === undefined || base.distance === null ? '' : String(base.distance).trim();
  const unitRaw = base.unit;
  const unit =
    unitRaw === undefined || unitRaw === null || String(unitRaw).trim() === '' ? 'meter' : String(unitRaw).trim();

  let date = 0;
  const dateRaw = base.date;
  if (typeof dateRaw === 'number' && Number.isFinite(dateRaw)) {
    date = dateRaw;
  } else if (typeof dateRaw === 'string' && dateRaw.trim() !== '') {
    const n = Number(dateRaw);
    date = Number.isFinite(n) ? n : 0;
  }

  const resume = base.resume === undefined || base.resume === null ? '' : String(base.resume);
  const inputType = base.inputType === undefined || base.inputType === null ? '' : String(base.inputType);

  const patientName =
    base.patientName !== undefined && base.patientName !== null ? String(base.patientName) : undefined;
  const doctorName = base.doctorName !== undefined && base.doctorName !== null ? String(base.doctorName) : undefined;
  const roomId = base.roomId !== undefined && base.roomId !== null ? String(base.roomId) : undefined;

  const patientId = optionalNumber(base.patientId);
  const ratio = optionalNumber(base.ratio);

  const chromaticPresetId =
    base.chromaticPresetId !== undefined && base.chromaticPresetId !== null
      ? String(base.chromaticPresetId)
      : undefined;
  const chromaticPrimary =
    base.chromaticPrimary !== undefined && base.chromaticPrimary !== null ? String(base.chromaticPrimary) : undefined;
  const chromaticSecondary =
    base.chromaticSecondary !== undefined && base.chromaticSecondary !== null
      ? String(base.chromaticSecondary)
      : undefined;

  const examModeRaw = base.examMode;
  const normalizedExamMode = examModeRaw === 'oval' || examModeRaw === 'va' ? examModeRaw : undefined;

  const vaCompleted = base.vaCompleted === true;
  const ovalCompleted = base.ovalCompleted === true;
  const examModesPerformedRaw = base.examModesPerformed;
  const examModesPerformed = Array.isArray(examModesPerformedRaw)
    ? examModesPerformedRaw.filter((mode): mode is 'va' | 'oval' => mode === 'va' || mode === 'oval')
    : undefined;

  const infantOutcome = coerceInfantOutcome(base.infantOutcome ?? base.infant_outcome);
  const testTypeRaw = base.testType ?? base.test_type;
  const testTypeFromField =
    typeof testTypeRaw === 'string' && VALID_PATIENT_TEST_TYPES.has(testTypeRaw) ? testTypeRaw : undefined;
  const testType = testTypeFromField ?? (infantOutcome ? 'infant' : undefined);

  return withLegacyChromaticForResult({
    distance,
    unit,
    footSteps: optionalNumber(base.footSteps),
    date,
    rightEye: coerceEyeBranch(pickEyeBranch(base, 'right')),
    leftEye: coerceEyeBranch(pickEyeBranch(base, 'left')),
    resume,
    inputType,
    patientName,
    patientId,
    doctorName,
    roomId,
    ratio,
    chromaticPresetId,
    chromaticPrimary,
    chromaticSecondary,
    examMode: normalizedExamMode,
    vaCompleted,
    ovalCompleted,
    examModesPerformed,
    rightEyeRefraction: coerceRefractionBranch(pickRefractionBranch(base, 'right')),
    leftEyeRefraction: coerceRefractionBranch(pickRefractionBranch(base, 'left')),
    cylinder: optionalNumber(base.cylinder),
    axis: optionalNumber(base.axis),
    ovalMinorRatio: optionalNumber(base.ovalMinorRatio),
    ovalAxisDeg: optionalNumber(base.ovalAxisDeg),
    testType,
    infantOutcome,
  });
}

// Formats a scalar history field for display; empty strings become an em dash.
export function formatHistoryScalar(value: string | number | undefined | null): string {
  if (value === undefined || value === null) {
    return '—';
  }
  const s = String(value).trim();
  return s === '' ? '—' : s;
}

// Formats arc-minute column when eye metrics exist and size is meaningful.
export function formatHistoryArcMinute(size: number, eye: { meters: string; feet: string; dec: string }): string {
  const hasMetrics = Boolean(eye.meters.trim() || eye.feet.trim() || eye.dec.trim());
  if (!hasMetrics || !Number.isFinite(size) || size <= 0) {
    return '—';
  }
  return String(size);
}
